/**
 * ReferencePicker : recherche tolérante de références (cuvée × millésime × format)
 * avec navigation clavier ↑/↓/Entrée. Réutilisé pour le rapprochement de facture.
 */
import { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { scannerApi } from '../../services/api';
import { WineColorDot } from './wine';
import { SEARCH_DEBOUNCE_MS } from '../../constants';

export function ReferencePicker({ onSelect, placeholder = 'Chercher une référence…', testId = 'reference-picker' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      scannerApi.searchRefs(q).then((r) => { setResults(r); setActive(0); }).catch(() => setResults([]));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const choose = (r) => { onSelect(r); setQ(''); setResults([]); };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); choose(results[active]); }
    else if (e.key === 'Escape') { setQ(''); setResults([]); }
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testId}>
      <Search className="w-4 h-4 text-n500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.6} />
      <input className="vd-input pl-9 h-9" placeholder={placeholder} value={q}
        onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} data-testid={`${testId}-input`} />
      {results.length > 0 && (
        <div className="absolute z-40 mt-1 w-full bg-paper border border-hair rounded-md shadow-lg max-h-64 overflow-auto">
          {results.map((r, i) => (
            <button key={r.reference_id} onClick={() => choose(r)} onMouseEnter={() => setActive(i)}
              data-testid={`${testId}-option-${r.reference_id}`}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left ${i === active ? 'vd-selected' : 'hover:bg-surface'}`}>
              <WineColorDot color={r.color} size={9} />
              <span className="font-title text-base">{r.name}</span>
              <span className="text-[12px] text-n500 nums">{r.year} · {r.format_label}</span>
              <span className="ml-auto text-[12px] text-n500">{r.domain_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
